import './offer-details.scss';

import { Collapse, Tabs, type TabsProps } from 'antd';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';

import {
  AllurLoader,
  CircledImage,
  InfoBadge,
  Modal
} from '@/components/Shared/ui';
import { type BankNames, DictTypes } from '@/constants';
import { useAppSelector } from '@/store';

import useOfferHistory from './hooks/useOfferHistory';

enum TabKeys {
  mainInfo = 'main-info',
  allInfo = 'all-info',
  history = 'history'
}

interface IProps {
  bankCode: BankNames;
  bankResponse: {
    status?: string;
    description?: string;
    allResponse: string;
  };
  historyData?: {
    bank: string;
    offerId: string;
  };
  isOpened: boolean;
  onClose: (isOpened: boolean) => void;
}

const OfferDetails: React.FC<IProps> = ({
  bankCode,
  bankResponse,
  historyData,
  isOpened,
  onClose
}) => {
  const { dicts } = useAppSelector((state) => state.dict);
  const [activeTab, setActiveTab] = useState(TabKeys.allInfo);

  const { data, loading } = useOfferHistory({
    ...historyData,
    shouldSentRequest: !!historyData && activeTab === TabKeys.history
  });

  const bank = dicts?.[DictTypes.Banks]?.getBy.name[bankCode];

  const items: TabsProps['items'] = useMemo(() => {
    return [
      ...(bankResponse.status && bankResponse.description
        ? [
            {
              key: TabKeys.mainInfo,
              label: 'Информация',
              children: (
                <>
                  <p>
                    <b>Статус: {bankResponse.status}</b>
                  </p>
                  <p className="offer-details__info-description">
                    <b>Описание:</b> {bankResponse.description}
                  </p>
                </>
              )
            }
          ]
        : []),
      {
        key: TabKeys.allInfo,
        label: 'Полный ответ банка',
        children: (
          <pre className="offer-details__pre">{bankResponse.allResponse}</pre>
        )
      },
      ...(historyData
        ? [
            {
              key: TabKeys.history,
              label: 'История изменений',
              children: (
                <>
                  {loading ? (
                    <div className="offer-details__collapse">
                      <AllurLoader fullWidth />
                    </div>
                  ) : data.length === 0 ? (
                    <div className="offer-details__no-info">
                      <InfoBadge title="История изменений предложения пуста" />
                    </div>
                  ) : (
                    data.map((item) => (
                      <div
                        key={item.createdDate}
                        className="offer-details__collapse"
                      >
                        <Collapse
                          className=""
                          items={[
                            {
                              key: item.createdDate,
                              label: (
                                <div className="offer-details__collapse-header">
                                  <p>
                                    <b>{item.description}</b>
                                  </p>
                                  <p>
                                    {dayjs(item.createdDate).format(
                                      'DD.MM.YYYY, HH:mm:ss'
                                    )}
                                  </p>
                                </div>
                              ),
                              children: (
                                <div>
                                  <p>
                                    Инициатор: <b>{item.initiator}</b>
                                  </p>
                                  <pre className="offer-details__pre">
                                    {item.details}
                                  </pre>
                                </div>
                              )
                            }
                          ]}
                        />
                      </div>
                    ))
                  )}
                </>
              )
            }
          ]
        : [])
    ];
  }, [bankResponse, historyData, data, loading]);

  const handleClose = () => {
    onClose(false);
  };

  return (
    <Modal
      centered
      mask={{ closable: true }}
      className="offer-details"
      title={
        <div className="offer-details__title-wrapper">
          <h3 className="offer-details__title">Описание решения</h3>
          <div className="offer-details__title-bank">
            <CircledImage
              src={bank?.icon || bank?.name}
              alt={bankCode}
              size={40}
            />
            {bank?.fullname}
          </div>
        </div>
      }
      open={isOpened}
      onClose={handleClose}
      footer={null}
      width={800}
    >
      <Tabs
        activeKey={activeTab}
        items={items}
        onChange={(value) => setActiveTab(value as TabKeys)}
      />
    </Modal>
  );
};

export default OfferDetails;
